import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import type { TitleDetail, TitleDetailSeason } from '@/interfaces/title-details-graphql';

import { useI18n } from '@/helpers/composables/useI18n';
import { UpcomingReleaseLabel } from '@/@types/graphql-types';
import {
	isSeason,
	UpcomingReleaseForSpinningText,
	upcomingReleasesOrdered,
} from '@/components/buybox/utils/upcoming-helpers';


export default /*#__PURE__*/_defineComponent({
  __name: 'UpcomingSpinningSegment',
  props: {
    title: null,
    upcomingReleases: null,
    isTheatrical: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
	title: TitleDetail | TitleDetailSeason;
	upcomingReleases: UpcomingReleaseForSpinningText[];
	isTheatrical?: boolean;
};



const { t } = useI18n();

// Upcoming releases are grouped by label. For now, groups with label=Date build a sentence. All other upcoming offers have individual sentence. (WIP)
// e.g. [{date: '5th November 2024, providers: 'Hulu, Max', label: Date},
//      {date: '12th November 2024, providers: 'Amazon Prime,', label: Date},
//     {date: 'December 2024, providers: 'Crunchyroll', label: Month}]
// Title will be available to watch on Hulu, Max on 5th November, and on Amazon Prime on 12th November. Title will be available to watch on Crunchyroll in December 2024.

const upcomingReleasesGroupedByLabel = computed(() => {
	const sortedArray = [...props.upcomingReleases].sort((a, b) => {
		return (upcomingReleasesOrdered[a.label] ?? 10) - (upcomingReleasesOrdered[b.label] ?? 10);
	});

	return sortedArray.reduce((acc, item) => {
		if (!acc[item.label]) {
			acc[item.label] = [];
		}
		acc[item.label].push(item);
		return acc;
	}, {} as Record<UpcomingReleaseLabel, UpcomingReleaseForSpinningText[]>);
});

const upcomingText = computed(() => {
	return Object.values(upcomingReleasesGroupedByLabel.value)
		.map(group =>
			group
				.map((release, index) =>
					getReleaseLabel(
						release.label,
						release.date,
						release.providers,
						index === 0,
						group.length - 1 === index
					)
				)
				.join(' ')
		)
		.join(' ');
});

const year = computed(() => new Date().getFullYear());

function getReleaseLabel(
	label: UpcomingReleaseLabel,
	date: string,
	providers: string,
	isFirst: boolean,
	isLast?: boolean
) {
	const title = titleName.value;
	const provider = providers;
	if (props.isTheatrical) {
		if (label === UpcomingReleaseLabel.ToBeAnnounced)
			return t('WEBAPP_SPIN_UPCOMING_CINEMA_OFFER_TBA', { title, provider });
		if (label === UpcomingReleaseLabel.Today)
			return t('WEBAPP_SPIN_UPCOMING_CINEMA_OFFER_TODAY', { title, provider });
		if (label === UpcomingReleaseLabel.Tomorrow)
			return t('WEBAPP_SPIN_UPCOMING_CINEMA_OFFER_TMRW', { title, provider });

		if (isSeason(label))
			return label === UpcomingReleaseLabel.Spring
				? t('WEBAPP_SPIN_UPCOMING_CINEMA_OFFER_SEASON_SPRING', {
						title,
						provider,
						season: `${date} ${year.value}`,
				  })
				: t('WEBAPP_SPIN_UPCOMING_CINEMA_OFFER_SEASON', { title, provider, season: `${date} ${year.value}` });
		if (label === UpcomingReleaseLabel.Year)
			return t('WEBAPP_SPIN_UPCOMING_CINEMA_OFFER_YEAR', { title, provider, year: date });

		if (label === UpcomingReleaseLabel.Month)
			return t('WEBAPP_SPIN_UPCOMING_CINEMA_OFFER_MONTH', { title, provider, month: date });

		if (label === UpcomingReleaseLabel.Date) {
			const text = isFirst
				? t('WEBAPP_SPIN_UPCOMING_CINEMA_OFFER_DATE', { title, provider, date }).toString().replace(/\./g, '')
				: t('WEBAPP_SPIN_MULTIPLE_UPCOMING_OFFERS_DATE', { provider, date });

			return isLast ? `${text}.` : text;
		}
	}

	if (label === UpcomingReleaseLabel.ToBeAnnounced) return t('WEBAPP_SPIN_UPCOMING_OFFER_TBA', { title, provider });
	if (label === UpcomingReleaseLabel.Today) return t('WEBAPP_SPIN_UPCOMING_OFFER_TODAY', { title, provider });
	if (label === UpcomingReleaseLabel.Tomorrow) return t('WEBAPP_SPIN_UPCOMING_OFFER_TMRW', { title, provider });
	if (isSeason(label))
		return label === UpcomingReleaseLabel.Spring
			? t('WEBAPP_SPIN_UPCOMING_OFFER_SEASON_SPRING', { title, provider, season: `${date} ${year.value}` })
			: t('WEBAPP_SPIN_UPCOMING_OFFER_SEASON', { title, provider, season: `${date} ${year.value}` });
	if (label === UpcomingReleaseLabel.Year)
		return t('WEBAPP_SPIN_UPCOMING_OFFER_YEAR', { title, provider, year: date });
	if (label === UpcomingReleaseLabel.Month)
		return t('WEBAPP_SPIN_UPCOMING_OFFER_MONTH', { title, provider, month: date });
	if (label === UpcomingReleaseLabel.Date) {
		const text = isFirst
			? t('WEBAPP_SPIN_UPCOMING_OFFER_DATE', { title, provider, date }).toString().replace(/\./g, '')
			: t('WEBAPP_SPIN_MULTIPLE_UPCOMING_OFFERS_DATE', { provider, date });

		return isLast ? `${text}.` : text;
	}
	return t('WEBAPP_SPIN_UPCOMING_OFFER_NO_DATE', { title, provider });
}

const titleName = computed(() => {
	if ((props.title as TitleDetailSeason).show) {
		return `${(props.title as TitleDetailSeason).show.content.title} ${props.title.content.title}`;
	}

	return props.title.content.title;
});

return { __sfc: true,props, t, upcomingReleasesGroupedByLabel, upcomingText, year, getReleaseLabel, titleName }
}

})